import { useState, useRef, useEffect } from 'react';
import { cabinaDataByStation, type CabinaMessage } from '@/mocks/cabinaContent';
import { radioStations } from '@/mocks/radios';

export default function CabinaTab() {
  const [selectedStation, setSelectedStation] = useState(1);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const cabina = cabinaDataByStation[selectedStation];
  const station = radioStations.find((s) => s.id === selectedStation);

  // Get stations that have cabina data
  const stationsWithCabina = radioStations.filter(
    (s) => cabinaDataByStation[s.id],
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [cabina?.messages]);

  if (!cabina || !station) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-4">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-broadcast-line text-2xl text-foreground-400"></i>
        </div>
        <p className="text-foreground-500 text-sm">Cabina no disponible</p>
      </div>
    );
  }

  const sortedMessages = [...cabina.messages].reverse();

  const handleSend = () => {
    if (!newMessage.trim()) return;
    setNewMessage('');
    inputRef.current?.focus();
  };

  const statusColor =
    cabina.status === 'online'
      ? 'bg-accent-500'
      : cabina.status === 'away'
        ? 'bg-amber-400'
        : 'bg-foreground-400';

  return (
    <div className="flex flex-col h-full bg-background-50">
      {/* Header */}
      <div className="safe-top px-4 pt-4 pb-3">
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            <img
              src={cabina.hostPhotoUrl}
              alt={cabina.hostName}
              className="w-11 h-11 rounded-full object-cover"
            />
            <div
              className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 ${statusColor} rounded-full border-2 border-background-50`}
            ></div>
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-foreground-950 leading-tight">
              {cabina.programName}
            </h2>
            <p className="text-foreground-500 text-xs">
              {cabina.hostName} · {cabina.onlineListeners.toLocaleString()} oyentes
            </p>
          </div>
          {cabina.status === 'online' && (
            <div className="ml-auto flex items-center gap-1.5 bg-accent-500/10 px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse"></span>
              <span className="text-accent-600 text-[10px] font-bold">EN VIVO</span>
            </div>
          )}
        </div>
      </div>

      {/* Station selector */}
      <div className="px-4 pb-3 overflow-x-auto scrollbar-none">
        <div className="flex gap-2">
          {stationsWithCabina.map((s) => (
            <button
              key={s.id}
              onClick={() => setSelectedStation(s.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                selectedStation === s.id
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <div className="w-5 h-5 rounded-full overflow-hidden bg-background-200 shrink-0">
                <img src={s.logoUrl} alt={s.name} className="w-full h-full object-cover" />
              </div>
              {s.frequency}
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 pb-2 scrollbar-none">
        {sortedMessages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-2.5 mb-4 ${
              msg.from === cabina.hostName ? '' : 'flex-row-reverse'
            }`}
          >
            {msg.from !== cabina.hostName && msg.avatarUrl && (
              <img
                src={msg.avatarUrl}
                alt={msg.from}
                className="w-8 h-8 rounded-full object-cover shrink-0 self-end"
              />
            )}
            <div
              className={`max-w-[75%] ${
                msg.from === cabina.hostName ? '' : 'items-end'
              } flex flex-col`}
            >
              {msg.from !== cabina.hostName && (
                <span className="text-[10px] text-foreground-400 mb-0.5 px-1">
                  {msg.from}
                </span>
              )}
              <div
                className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.from === cabina.hostName
                    ? 'bg-primary-500 text-background-50 rounded-bl-md'
                    : 'bg-background-100 text-foreground-800 rounded-br-md'
                }`}
              >
                {msg.type === 'song_request' && (
                  <span className="inline-flex items-center gap-1 bg-background-50/20 text-[10px] font-semibold px-1.5 py-0.5 rounded-full mb-1">
                    <i className="ri-music-2-line text-[10px]"></i> Petición
                  </span>
                )}
                {msg.type === 'shoutout' && (
                  <span className="inline-flex items-center gap-1 bg-background-50/20 text-[10px] font-semibold px-1.5 py-0.5 rounded-full mb-1">
                    <i className="ri-megaphone-line text-[10px]"></i> Saludo
                  </span>
                )}
                {msg.type === 'contest' && (
                  <span className="inline-flex items-center gap-1 bg-background-50/20 text-[10px] font-semibold px-1.5 py-0.5 rounded-full mb-1">
                    <i className="ri-trophy-line text-[10px]"></i> Concurso
                  </span>
                )}
                {msg.type === 'voice' && (
                  <span className="inline-flex items-center gap-1 bg-background-50/20 text-[10px] font-semibold px-1.5 py-0.5 rounded-full mb-1">
                    <i className="ri-voiceprint-line text-[10px]"></i> Voz
                  </span>
                )}
                <p>{msg.text}</p>
              </div>
              <span className="text-[9px] text-foreground-400 mt-0.5 px-1">
                {msg.time}
              </span>
            </div>
            {msg.from === cabina.hostName && (
              <img
                src={cabina.hostPhotoUrl}
                alt={cabina.hostName}
                className="w-8 h-8 rounded-full object-cover shrink-0 self-end"
              />
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-background-200 bg-background-50 safe-bottom">
        <div className="flex items-center gap-2">
          <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center cursor-pointer shrink-0 text-foreground-500 hover:bg-background-200 transition-colors">
            <i className="ri-add-line text-lg"></i>
          </button>
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Escribe un mensaje..."
              className="w-full pl-4 pr-4 py-2.5 bg-background-100 border border-background-200 rounded-full text-foreground-950 text-sm placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
            />
          </div>
          <button
            onClick={handleSend}
            className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center cursor-pointer shrink-0 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-send-plane-fill text-background-50 text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}